import React, {Fragment, useCallback} from 'react';
import {Menu, Disclosure, Transition} from '@headlessui/react';
import type {AstView, EnrichedAstNode} from 'rendition-core';
import {
    RightClickMenuDropdown,
    RightClickMenuOption,
    useRightClickMenuContext,
} from 'Contexts/PreviewContext/RightClickMenuContext';
import ClickAwayListener from './ClickAwayListener';
import classNames from 'classnames';
import {useKeyBind} from 'Hooks/useKeyBind';
import {Chevron} from 'Icons/Chevron';

interface Props {
    view: AstView;
    selectedNodes: EnrichedAstNode[];
}

interface DropdownProps {
    option: RightClickMenuDropdown;
    isFirst?: boolean;
    isLast?: boolean;
}

const RightClickDropdown: React.FC<React.PropsWithChildren<DropdownProps>> = ({option, children, isLast, isFirst}) => {
    return (
        <Disclosure>
            {({open}) => (
                <>
                    <Disclosure.Button className="flex w-full flex-row flex-nowrap items-center justify-between fill-white px-2 py-1 text-[10px] hover:bg-sky-500/40">
                        {option.label}
                        <Chevron width={15} height={15} direction={open ? 'up' : 'down'} />
                    </Disclosure.Button>
                    <Disclosure.Panel className="pl-2">{children}</Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
};

interface ItemProps {
    option: RightClickMenuOption;
    isDisabled: boolean;
    index: number;
    inDropdown?: boolean;
    nodes: EnrichedAstNode[];
    isFirst?: boolean;
    isLast?: boolean;
    onClick: () => void;
}

const RightClickMenuItem: React.FC<ItemProps> = ({option, nodes, isDisabled, index, inDropdown, onClick}) => {
    return (
        <Menu.Item>
            {(active) => (
                <button
                    className={classNames(
                        'flex w-full appearance-none items-start px-2 py-1 text-[10px] hover:bg-sky-500/30 disabled:opacity-30',
                        isDisabled && 'opacity-50'
                    )}
                    key={option.label}
                    disabled={isDisabled}
                    onClick={() => {
                        onClick();
                        option.handler(nodes);
                    }}
                >
                    <span className="">{option.label}</span>
                </button>
            )}
        </Menu.Item>
    );
};

export const RightClickMenu: React.FC<React.PropsWithChildren<Props>> = ({view, selectedNodes}) => {
    const {rightClickMenuOptions, rightClickMenuOpen, setRightClickMenuOpen, rightClickMenuCoordinates} =
        useRightClickMenuContext();

    const isDisabled = useCallback(
        (condition: RightClickMenuOption['condition']) => {
            if (!selectedNodes.length) {
                return false;
            }
            return condition ? !condition(selectedNodes) : false;
        },
        [selectedNodes]
    );

    const sortDisabledItems = (a: {isDisabled: boolean}, b: {isDisabled: boolean}) => {
        if (a.isDisabled && !b.isDisabled) {
            return 1;
        } else if (!a.isDisabled && b.isDisabled) {
            return -1;
        } else {
            return 0;
        }
    };

    const options = rightClickMenuOptions
        .filter((option) => {
            if (!option.dropdown) {
                return true;
            }

            const allHandlersDisabled = option.handlers.every((handler) => isDisabled(handler.condition));
            const dropdownDisabled = isDisabled(option.condition);
            return !allHandlersDisabled && !dropdownDisabled;
        })
        .sort(({condition, dropdown}) => {
            const disabled = isDisabled(condition);
            // We want to show the menu options that are active at the top
            return disabled ? 1 : -1;
        })
        .sort(({dropdown}) => (dropdown ? 1 : -1));

    const closeMenu = useCallback(() => {
        setRightClickMenuOpen(false);
    }, [setRightClickMenuOpen]);

    useKeyBind('escape', closeMenu);

    return (
        <Menu>
            <ClickAwayListener
                onClickAway={() => {
                    setRightClickMenuOpen(false);
                }}
            >
                <Transition
                    show={rightClickMenuOpen && selectedNodes.length > 0}
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                >
                    <Menu.Items
                        className={classNames(
                            'fixed z-[1001] m-0 rounded-md bg-neutral-600 p-2 text-neutral-300 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                            // Prevent the menu from flashing option changes as selected nodes change
                            !selectedNodes.length && 'hidden'
                        )}
                        static
                        style={{
                            top: rightClickMenuCoordinates?.y,
                            left: rightClickMenuCoordinates?.x,
                        }}
                    >
                        {options
                            .map((option) => {
                                let isOptionDisabled = false;
                                if ('condition' in option) {
                                    isOptionDisabled = isDisabled(option.condition);
                                }

                                return {
                                    ...option,
                                    isDisabled: isOptionDisabled,
                                };
                            })
                            .sort(sortDisabledItems)
                            .map((option, index) => {
                                const isFirst = index === 0;
                                const isLast = index === options.length - 1;
                                if (option.dropdown) {
                                    const dropdownHandlers = option.handlers.filter(
                                        (handler) => !isDisabled(handler.condition)
                                    );
                                    return (
                                        <RightClickDropdown
                                            key={option.label}
                                            option={option}
                                            isFirst={isFirst}
                                            isLast={isLast}
                                        >
                                            {dropdownHandlers
                                                .map((handler) => ({
                                                    handler: handler,
                                                    isDisabled: isDisabled(handler.condition),
                                                }))
                                                .sort(sortDisabledItems)
                                                .map((data, i) => (
                                                    <RightClickMenuItem
                                                        onClick={closeMenu}
                                                        isDisabled={data.isDisabled}
                                                        option={data.handler}
                                                        index={index}
                                                        key={data.handler.label}
                                                        nodes={selectedNodes}
                                                        isFirst={isFirst}
                                                        isLast={isLast && i === dropdownHandlers.length - 1}
                                                    />
                                                ))}
                                        </RightClickDropdown>
                                    );
                                }

                                return (
                                    <RightClickMenuItem
                                        onClick={closeMenu}
                                        isDisabled={isDisabled(option.condition)}
                                        option={option}
                                        index={index}
                                        key={option.label}
                                        nodes={selectedNodes}
                                        isFirst={isFirst}
                                        isLast={isLast}
                                    />
                                );
                            })}
                    </Menu.Items>
                </Transition>
            </ClickAwayListener>
        </Menu>
    );
};
